import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import { Box, Divider, Text } from 'components/ui';
import store from 'Store';
import checkoutApi from 'services/api/checkout';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import decorators from 'utils/decorators';
import UtilActions from 'utils/redux/Actions';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import klarnaUtils from 'utils/Klarna';
import KlarnaActions from 'actions/KlarnaActions';
import OrderActions from 'actions/OrderActions';
import Actions from 'Actions';
import OrderUtils from 'utils/Order';
import localeUtils from 'utils/LanguageLocale';
import { forms } from 'style/config';
import DefaultPaymentCheckbox from 'components/RwdCheckout/Sections/Payment/Section/DefaultPaymentCheckbox';
import mediaUtils from 'utils/Media';
import KlarnaPaymentMethodEN from 'components/Klarna/KlarnaPaymentMethod/locales/KlarnaPaymentMethod_en_US';
import KlarnaPaymentMethodFR from 'components/Klarna/KlarnaPaymentMethod/locales/KlarnaPaymentMethod_fr_CA';
import { colors } from 'style/config';

const { Media } = mediaUtils;

const KLARNA_LOAD_TIMEOUT = 15000;
const KLARNA_IFRAME_WRAPPER_ID = 'klarna_iframe_wrapper';

class KlarnaPaymentMethod extends BaseClass {
    constructor(props) {
        super(props);
        const orderDetails = store.getState().order.orderDetails;
        const isBopisOrder = orderDetails.header && orderDetails.header.isBopisOrder;

        this.state = {
            useMyShippingAddress: false,
            isBopisOrder
        };

        // In frictionless mode, track if we need to skip the next amount change to prevent duplicates
        // This will be set to true when we manually call loadIframe (either on mount or re-selection)
        this.skipNextAmountChange = false;
    }

    timeout = null;

    getLocale = () => {
        const isCanada = localeUtils.isCanada();
        const isFrench = localeUtils.isFrench();

        if (isCanada) {
            return isFrench ? 'fr_ca' : 'en_ca';
        }

        return 'en_us';
    };

    initKlarna = async () => {
        const session = await checkoutApi.initializeKlarnaCheckout({ status: klarnaUtils.SESSION_STATUSES.UPDATE });
        const order = await checkoutApi.getOrderDetails(OrderUtils.getOrderId());
        store.dispatch(OrderActions.updateOrder(order));
        store.dispatch(KlarnaActions.persistEmdFromSession(session));

        return {
            session,
            order
        };
    };

    toggleCheckbox = () => {
        store.dispatch(KlarnaActions.toggleShipping(!this.state.useMyShippingAddress));
    };

    loadIframe = () => {
        const getText = localeUtils.getLocalisedDataFromResource(KlarnaPaymentMethodEN, KlarnaPaymentMethodFR);
        const onFailure = error => {
            // eslint-disable-next-line no-console
            console.error(error);
            clearTimeout(this.timeout);
            store.dispatch(KlarnaActions.showError(getText('iframeError'), true));
            store.dispatch(KlarnaActions.setInitializing(false));
        };
        store.dispatch(UtilActions.merge('klarna', 'isReady', false));
        store.dispatch(KlarnaActions.setInitializing(true));

        return decorators
            .withInterstice(this.initKlarna, INTERSTICE_DELAY_MS)()
            .then(data => {
                if (this.isUnmounted) {
                    store.dispatch(KlarnaActions.setInitializing(false));

                    return;
                }

                if (!data.session || !data.session.clientToken) {
                    onFailure({ reason: 'clientToken' });
                }

                // call reject if klarna API is not responding
                if (this.timeout) {
                    clearTimeout(this.timeout);
                }

                this.timeout = setTimeout(() => {
                    onFailure({ reason: 'timeout' });
                }, KLARNA_LOAD_TIMEOUT);

                klarnaUtils
                    .load(KLARNA_IFRAME_WRAPPER_ID, data.session.clientToken)
                    .then(() => {
                        store.dispatch(UtilActions.merge('klarna', 'isReady', true));
                        store.dispatch(KlarnaActions.setInitializing(false));
                    })
                    .catch(error => {
                        onFailure(error);
                    })
                    .finally(() => {
                        clearTimeout(this.timeout);
                    });
            })
            .catch(err => {
                store.dispatch(Actions.showInterstice(false));
                onFailure(err);
            });
    };

    componentDidMount() {
        store.setAndWatch('order.orderDetails.priceInfo.creditCardAmount', this, null, true);
        store.setAndWatch('order.orderDetails.priceInfo.paypalAmount', this, null, true);
        store.setAndWatch('klarna.useMyShippingAddress', this, null, true);

        // In frictionless mode, use backgroundInit to load into 'backgroundPaymentWidget'
        // Only call it if there's NO Klarna payment group yet AND not already initializing
        // In regular checkout, use loadIframe to load into 'klarna_iframe_wrapper'
        if (this.props.isFrictionless) {
            const klarnaState = store.getState().klarna;
            const orderDetails = store.getState().order.orderDetails;
            const hasKlarnaPaymentGroup = OrderUtils.getPaymentGroup(orderDetails, OrderUtils.PAYMENT_GROUP_TYPE.KLARNA);

            // Only initialize if: no payment group exists AND not already initializing
            if (!hasKlarnaPaymentGroup && !klarnaState.isInitializing) {
                // No payment group exists yet, so create it
                const getText = localeUtils.getLocalisedDataFromResource(KlarnaPaymentMethodEN, KlarnaPaymentMethodFR);
                const errorMessage = getText('iframeError');

                // Set flag to skip the next amount change from this API call's order update
                this.skipNextAmountChange = true;
                store.dispatch(KlarnaActions.backgroundInit(errorMessage));
            }
            // If payment group already exists, checkSelectedPayment will handle calling backgroundInit
        } else {
            this.skipNextAmountChange = true;
            this.loadIframe();
        }
    }

    componentWillUnmount() {
        // if component is unmount -> void loadCallId
        // so the callbacks of Klarna.Payments.load() will not be handled
        klarnaUtils.loadCallId = 0;
        this.isUnmounted = true;
        clearTimeout(this.timeout);
        // Reset isInitializing flag to prevent blocking future Klarna initialization
        store.dispatch(KlarnaActions.setInitializing(false));
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.creditCardAmount !== this.state.creditCardAmount || prevState.paypalAmount !== this.state.paypalAmount) {
            // Skip the first amount change triggered by the componentDidMount loadIframe call's order update
            // to prevent duplicate API calls. All subsequent amount changes should update the session normally.
            if (this.skipNextAmountChange) {
                this.skipNextAmountChange = false;

                return;
            }

            // Only call loadIframe if not already initializing (prevents race conditions)
            const klarnaState = store.getState().klarna;

            if (!klarnaState.isInitializing) {
                this.loadIframe();
            }
        }
    }

    render() {
        const getText = localeUtils.getLocalisedDataFromResource(KlarnaPaymentMethodEN, KlarnaPaymentMethodFR);
        const { isAnonymous, isFrictionless } = this.props;

        const klarnaLocale = this.getLocale();
        const klarnaTermsUrl = `https://cdn.klarna.com/1.0/shared/content/legal/terms/0/${klarnaLocale}/user`;
        const klarnaPrivacyUrl = `https://cdn.klarna.com/1.0/shared/content/legal/terms/0/${klarnaLocale}/privacy`;
        // For fr_CA we read a hardcoded key that has no _en_US.js counterpart so SDL translation cannot overwrite it
        const legalNotice = localeUtils.isFRCanada() ? getText('legalNoticeHardcoded') : getText('legalNotice', [klarnaTermsUrl, klarnaPrivacyUrl]);

        return (
            <>
                {!isFrictionless && (
                    <Box
                        fontSize={['xl', '2xl']}
                        marginBottom={[4, 3]}
                    ></Box>
                )}
                <Box
                    id={KLARNA_IFRAME_WRAPPER_ID}
                    marginY={[0, 1]}
                    maxWidth={410}
                    minHeight={'auto'}
                    {...(isFrictionless && { marginLeft: [0, 6] })}
                ></Box>
                {!isAnonymous && (
                    <Media lessThan='sm'>
                        <Divider marginY={4} />
                    </Media>
                )}
                <Box
                    marginLeft={[0, forms.RADIO_SIZE + forms.RADIO_MARGIN]}
                    {...(isFrictionless && { marginBottom: [4, 0] })}
                >
                    {!this.state.isBopisOrder && (
                        <Checkbox
                            paddingY={3}
                            checked={!!this.state.useMyShippingAddress}
                            onClick={this.toggleCheckbox}
                        >
                            {getText('myBillingAddrIsTheSame')}
                        </Checkbox>
                    )}
                    {!isAnonymous && <DefaultPaymentCheckbox paymentName={'klarna'} />}
                    <Text
                        is='p'
                        key='klarnaLegalNotice'
                        fontSize='sm'
                        css={styles.legal}
                        dangerouslySetInnerHTML={{ __html: legalNotice }}
                    />
                </Box>
            </>
        );
    }
}

const styles = {
    legal: {
        '& a': {
            color: colors.blue,
            textDecoration: 'underline'
        }
    }
};

export default wrapComponent(KlarnaPaymentMethod, 'KlarnaPaymentMethod', true);
